import Flag from "@mui/icons-material/Flag";
import { Box, Card, type CardProps, Grid, TextField } from "@mui/material";
import type { FC } from "react";
import {
  Controller,
  type ControllerFieldState,
  type FieldError,
  useFormContext,
} from "react-hook-form";
import type { ProgramsState } from "../hooks/use-programs";
import type { MomentFormMoment } from "../types/moment-form-moment";
import { ImageSelector } from "./image-selector/image-selector";
import { ProgramSelect } from "./program-selector/program-selector";

interface BasicFormProps extends CardProps {
  formDisabled: boolean;
  setUnsplashDownloadUrl: (url?: string) => void;
  programs: ProgramsState;
}

/** Form for editing moment's direct fields */
export const BasicForm: FC<BasicFormProps> = ({
  formDisabled,
  setUnsplashDownloadUrl,
  programs,
  ...cardProps
}) => {
  const { control } = useFormContext<MomentFormMoment>();

  const validationError = (
    fieldState: ControllerFieldState,
    submitted: boolean,
  ): FieldError | undefined => {
    if (!fieldState.error) return;

    return submitted || fieldState.isTouched ? fieldState.error : undefined;
  };

  return (
    <Card
      {...cardProps}
      sx={{ minWidth: "100%", mb: 3, bgcolor: "background.default", ...cardProps.sx }}
    >
      <Grid container spacing={4}>
        <Grid size={{ xs: 1 }}>
          <Box
            sx={{
              backgroundColor: "background.contrast",
              pr: 3,
              pl: 2.2,
              py: 2,
              width: 64,
            }}
          >
            <Flag sx={{ width: "30px", height: "30px" }} />
          </Box>
        </Grid>
        <Grid size={{ xs: 7 }} my={4}>
          <>
            <Grid container flexDirection="row" spacing={2}>
              <Grid size={{ xs: 7.5 }} sx={{ mt: 1 }}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field, fieldState, formState: { submitCount } }) => (
                    <TextField
                      {...field}
                      variant="standard"
                      label="Communication Name"
                      helperText={
                        validationError(fieldState, submitCount > 0)?.message ||
                        "Provide a unique name for your Communication"
                      }
                      error={Boolean(validationError(fieldState, submitCount > 0))}
                      disabled={formDisabled}
                      fullWidth
                      id="moment-name-input"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 4.5 }}>
                <Controller
                  name="program_id"
                  control={control}
                  render={({ field, fieldState, formState: { submitCount } }) => (
                    <ProgramSelect
                      value={field.value ?? undefined}
                      disabled={formDisabled}
                      errorMessage={validationError(fieldState, submitCount > 0)?.message}
                      onChange={field.onChange}
                      programs={programs}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState, formState: { submitCount } }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    variant="standard"
                    sx={{ mt: 2 }}
                    label="Description"
                    helperText={validationError(fieldState, submitCount > 0)?.message}
                    error={Boolean(validationError(fieldState, submitCount > 0))}
                    disabled={formDisabled}
                    multiline
                    rows={5}
                    fullWidth
                    id="description-input"
                  />
                )}
              />
            </Grid>
          </>
        </Grid>
        <Grid sx={{ mt: 5 }}>
          <Controller
            name="cover_image_path"
            control={control}
            render={({ field }) => (
              <ImageSelector
                url={field.value || ""}
                onImageSelected={({ url, downloadUrl }: { url?: string; downloadUrl?: string }) => {
                  field.onChange(url ?? null);
                  setUnsplashDownloadUrl(downloadUrl);
                }}
                disabled={formDisabled}
              />
            )}
          />
        </Grid>
      </Grid>
    </Card>
  );
};
